import { Link } from 'react-router-dom'
import { CarFront, MessageSquareText, Route, UserCog } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDateTime, OPERATIONS_TIME_ZONE_LABEL } from '@/lib/datetime'
import { formatCurrency } from '@/lib/format'
import type { AdminRating } from '@/types/api'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { isPositiveTag, moodLabel, tagLabel } from './describe'
import { RatingStars } from './RatingStars'
import styles from './RatingsPage.module.css'

interface RatingDetailsDrawerProps {
  rating: AdminRating | null
  onClose: () => void
}

/**
 * One rating, in full.
 *
 * The list truncates the comment; this is where it is read whole, beside the
 * trip it is about — because "the driver was late" means something different
 * on a 6 a.m. airport run than on a Friday afternoon in Zamalek. Links out to
 * the trip and the driver rather than repeating their records here.
 */
export function RatingDetailsDrawer({ rating, onClose }: RatingDetailsDrawerProps) {
  const open = rating !== null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="drawer"
      title={rating ? `${rating.rating}-star rating` : 'Rating'}
      description="What the customer said about this trip, and the trip it was about."
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {rating ? (
        <div className={styles.drawerBody}>
          <section className={cn(styles.verdict, rating.low && styles.verdictLow)}>
            <RatingStars value={rating.rating} size="lg" />
            <p className={styles.verdictWord}>{moodLabel(rating.rating)}</p>
            <p className={styles.verdictMeta}>
              Rated {formatDateTime(rating.createdAt)} · {OPERATIONS_TIME_ZONE_LABEL} time
            </p>
          </section>

          <section className={styles.drawerSection}>
            <h3 className={styles.drawerSectionTitle}>
              <MessageSquareText aria-hidden="true" />
              What they said
            </h3>
            {rating.tags.length > 0 ? (
              <div className={styles.tagRow}>
                {rating.tags.map((tag) => (
                  <span key={tag} className={cn(styles.tag, isPositiveTag(tag) ? styles.tagPositive : styles.tagNegative)}>
                    {tagLabel(tag)}
                  </span>
                ))}
              </div>
            ) : null}
            {rating.comment ? (
              <blockquote className={styles.quote}>{rating.comment}</blockquote>
            ) : (
              <p className={styles.muted}>
                {rating.tags.length > 0 ? 'No written comment — just the reasons above.' : 'A score only, with no reasons or comment.'}
              </p>
            )}
          </section>

          <section className={styles.drawerSection}>
            <h3 className={styles.drawerSectionTitle}>
              <UserCog aria-hidden="true" />
              Who
            </h3>
            <KeyValueGrid>
              <KeyValue label="Driver">
                {rating.driverName ? (
                  <span className={styles.person}>
                    <Avatar name={rating.driverName} src={rating.driverImageUrl} size="sm" />
                    <span>
                      {rating.driverName}
                      {rating.driverId != null ? <div className={styles.muted}>ID {rating.driverId}</div> : null}
                    </span>
                  </span>
                ) : (
                  '—'
                )}
              </KeyValue>
              <KeyValue label="Customer">
                {rating.customerName ?? '—'}
                {rating.customerPhone ? <div className={styles.muted}>{rating.customerPhone}</div> : null}
              </KeyValue>
            </KeyValueGrid>
          </section>

          <section className={styles.drawerSection}>
            <h3 className={styles.drawerSectionTitle}>
              <Route aria-hidden="true" />
              The trip
            </h3>
            <KeyValueGrid>
              <KeyValue label="Booking">
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {rating.bookingReference ?? (rating.tripId != null ? `#${rating.tripId}` : '—')}
                </span>
              </KeyValue>
              <KeyValue label="Package">{rating.packageName ?? '—'}</KeyValue>
              <KeyValue label="Pickup" wide>
                {rating.pickupAddress ?? '—'}
              </KeyValue>
              <KeyValue label="Destination" wide>
                {rating.dropoffAddress ?? '—'}
              </KeyValue>
              <KeyValue label="Scheduled start">{formatDateTime(rating.tripStartTime)}</KeyValue>
              <KeyValue label="Ended">{formatDateTime(rating.tripEndedAt)}</KeyValue>
              <KeyValue label="Trip price">{formatCurrency(rating.tripTotalPrice)}</KeyValue>
            </KeyValueGrid>
            <div className={styles.drawerLinks}>
              {rating.bookingReference ? (
                <Link
                  to={`/trips?search=${encodeURIComponent(rating.bookingReference)}`}
                  className={styles.drawerLink}
                  onClick={onClose}
                >
                  <CarFront aria-hidden="true" />
                  Open the trip
                </Link>
              ) : null}
              {rating.driverId != null ? (
                <Link to={`/ratings?driverId=${rating.driverId}`} className={styles.drawerLink} onClick={onClose}>
                  <UserCog aria-hidden="true" />
                  All ratings for this driver
                </Link>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  )
}
